import React, { useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { getOffersList, updateOfferStatus } from '../services/api';
import { OfferListFilters, OfferListResponse, StatusFilter, SyncOn } from '../types';
import useFilters, { IFilter } from '@shared/hooks/useFilters';
import { OrderType } from '@shared/interface';
import { saveFiltersToCookie, loadFiltersFromCookie, deleteCookie } from '@core/utils/cookieUtils';

type OfferFilterType = IFilter<OfferListFilters>;

const FILTER_COOKIE_KEY = 'offer_filters';
const EXCLUDED_URL_PARAMS = ['page', 'limit', 'orderBy', 'orderType'];

// Empty filter state for reset
const EMPTY_FILTERS: Partial<OfferListFilters> = {
  search: '',
  sync_on: '',
  status: '',
  dateFrom: '',
  dateTo: '',
  category: undefined,
  currency: undefined,
  scope: undefined,
  country: undefined,
};

// Helper to parse integer from search params
const parseIntParam = (value: string | null): number | undefined => {
  return value ? Number.parseInt(value, 10) : undefined;
};

// Helper to get filter value from URL or fallback
const getFilterValue = <T>(searchParams: URLSearchParams, key: string, parser?: (value: string) => T): T | string => {
  const value = searchParams.get(key);
  if (!value) return '' as T;
  return parser ? parser(value) : (value as T);
};

export const useOfferList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Base default filter (without saved filters) - this is what reset should return to
  const baseDefaultFilter = React.useMemo(
    (): OfferFilterType => ({
      page: Number.parseInt(searchParams.get('page') || '1', 10),
      limit: Number.parseInt(searchParams.get('limit') || '10', 10),
      orderBy: searchParams.get('orderBy') || '',
      orderType: (searchParams.get('orderType') as OrderType) || null,
      search: getFilterValue(searchParams, 'search'),
      sync_on: getFilterValue(searchParams, 'sync_on') as SyncOn | '',
      status: getFilterValue(searchParams, 'status') as StatusFilter | '',
      dateFrom: getFilterValue(searchParams, 'dateFrom'),
      dateTo: getFilterValue(searchParams, 'dateTo'),
      category: parseIntParam(searchParams.get('category')),
      currency: parseIntParam(searchParams.get('currency')),
      scope: parseIntParam(searchParams.get('scope')),
      country: parseIntParam(searchParams.get('country')),
    }),
    [searchParams],
  );

  // Load saved filters from cookie only if URL params are empty
  const initialFilter = React.useMemo(() => {
    // Check if URL has any filter params (excluding pagination/sorting)
    const hasUrlFilterParams = Array.from(searchParams.keys()).some((key) => !EXCLUDED_URL_PARAMS.includes(key));

    // If URL has filter params, prioritize them over cookie
    if (hasUrlFilterParams) {
      return baseDefaultFilter;
    }

    // Otherwise, merge saved filters from cookie
    const savedFilters = loadFiltersFromCookie<OfferListFilters>(FILTER_COOKIE_KEY);
    return savedFilters ? { ...baseDefaultFilter, ...savedFilters } : baseDefaultFilter;
  }, [baseDefaultFilter, searchParams]);

  const { filter, clear, changePage, sort, changePageSize, changeFilters } =
    useFilters<OfferListFilters>(initialFilter);

  // Clear all filters including cookie
  const clearFilter = React.useCallback(() => {
    deleteCookie(FILTER_COOKIE_KEY);
    changeFilters(EMPTY_FILTERS);
  }, [changeFilters]);

  // Sync filters to URL and cookie
  useEffect(() => {
    const params = new URLSearchParams();

    // Build URL params from non-empty filter values
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, String(value));
      }
    });

    setSearchParams(params, { replace: true });
    saveFiltersToCookie(FILTER_COOKIE_KEY, filter, EXCLUDED_URL_PARAMS);
  }, [filter, setSearchParams]);

  // Convert filter to API format
  const apiFilters: OfferListFilters = React.useMemo(
    () => ({
      page: filter.page,
      limit: filter.limit,
      order_by: filter.orderBy,
      sort_by: filter.orderType,
      search: filter.search || '',
      sync_on: filter.sync_on || '',
      status: filter.status || '',
      dateFrom: filter.dateFrom || '',
      dateTo: filter.dateTo || '',
      category: filter.category ?? null,
      currency: filter.currency ?? null,
      scope: filter.scope ?? null,
      country: filter.country ?? null,
    }),
    [filter],
  );

  // Query for offers
  const {
    data: offersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<OfferListResponse, Error>({
    queryKey: ['offers', apiFilters],
    queryFn: () => getOffersList(apiFilters),
    placeholderData: (prev) => prev,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Status toggle mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => updateOfferStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    changePage(newPage + 1); // Convert from 0-indexed to 1-indexed
  };

  // Handle sorting
  const handleSort = (property: string) => {
    if (filter.orderBy !== property) {
      // If new column, start with ASC
      sort({ orderBy: property, orderType: OrderType.ASC });
    } else if (filter.orderType === OrderType.ASC) {
      // If currently ASC, switch to DESC
      sort({ orderBy: property, orderType: OrderType.DESC });
    } else if (filter.orderType === OrderType.DESC) {
      // If currently DESC, reset sorting
      sort({ orderBy: '', orderType: null });
    }
  };

  // Handle navigation with state preservation
  const navigateToOffer = (path: string) => {
    navigate(path, {
      state: {
        fromList: true,
        currentPage: filter.page,
        currentFilters: filter,
      },
    });
  };

  // Handle status toggle
  const handleToggleOfferStatus = async (offerId: number, currentStatus: string) => {
    const apiStatus = currentStatus === 'staging' ? 'staging' : 'prod';
    try {
      await statusMutation.mutateAsync({ id: offerId, status: apiStatus });
      return true;
    } catch (error) {
      console.error('Failed to toggle offer status:', error);
      return false;
    }
  };

  // Prefetch the next page
  useEffect(() => {
    if (offersData?.data.currentPage < offersData?.data.totalPages) {
      const nextPageFilters = { ...apiFilters, page: filter.page + 1 };
      queryClient.prefetchQuery({
        queryKey: ['offers', nextPageFilters],
        queryFn: () => getOffersList(nextPageFilters),
      });
    }
  }, [filter, offersData, queryClient, apiFilters]);

  return {
    // Data
    offers: offersData?.data.data || [],
    totalRows: offersData?.data.totalRecords || 0,
    currentPage: offersData?.data.currentPage || 1,
    totalPages: offersData?.data.totalPages || 1,

    // Pagination (convert to 0-indexed for MUI)
    page: Math.max(0, (filter.page || 1) - 1),
    rowsPerPage: filter.limit || 10,

    // Sorting (convert API field name back to frontend column ID)
    orderBy: filter.orderBy || '',
    order: filter.orderType === OrderType.ASC ? ('asc' as const) : ('desc' as const),

    // Filters
    filters: filter,

    // Loading states
    isLoading,
    isError,
    error,
    isUpdatingStatus: statusMutation.isPending,

    // Actions
    handlePageChange,
    changePageSize,
    handleSort,
    changeFilters,
    clearFilter,
    refetch,

    // Navigation
    navigateToOffer,
    handleToggleOfferStatus,
  };
};
